import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaTimes, FaFilter, FaTags } from 'react-icons/fa';
import '../styles/EventSearch.css';

const EventSearch = ({ onEventSelect, onClose }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Obtener todos los eventos del mismo archivo que usa el timeline
    const allEvents = useMemo(() => {
        const events = Object.entries(t('lore.events', { returnObjects: true }))
            .map(([key, event]) => ({
                year: event.year,
                title: event.title,
                content: event.content,
                categoria: event.type,
                etiquetas: event.tags || {},
                origen: 'events-originales',
                codigo: key,
                eventKey: `lore.events.${key}`
            }));
        return events;
    }, [t]);

    // Extraer todas las etiquetas únicas por categoría
    const allTagsByCategory = useMemo(() => {
        const tags = {
            nombres: new Set(),
            planetas: new Set(),
            sistemas: new Set(),
            razas: new Set(),
            organizaciones: new Set()
        };

        allEvents.forEach(event => {
            if (event.etiquetas) {
                // Si etiquetas es un array (formato antiguo), convertirlo
                if (Array.isArray(event.etiquetas)) {
                    event.etiquetas.forEach(tag => {
                        if (tag && tag.trim()) {
                            tags.nombres.add(tag.trim());
                        }
                    });
                } else {
                    // Formato nuevo con categorías
                    Object.entries(event.etiquetas).forEach(([category, tagList]) => {
                        if (Array.isArray(tagList)) {
                            tagList.forEach(tag => {
                                if (tag && tag.trim()) {
                                    tags[category].add(tag.trim());
                                }
                            });
                        }
                    });
                }
            }
        });

        // Convertir Sets a Arrays ordenados
        return {
            nombres: Array.from(tags.nombres).sort(),
            planetas: Array.from(tags.planetas).sort(),
            sistemas: Array.from(tags.sistemas).sort(),
            razas: Array.from(tags.razas).sort(),
            organizaciones: Array.from(tags.organizaciones).sort()
        };
    }, [allEvents]);

    // Filtrar eventos
    const filteredEvents = useMemo(() => {
        return allEvents.filter(event => {
            // Filtro por categoría
            if (selectedCategory !== 'all' && event.categoria !== selectedCategory) {
                return false;
            }

            // Filtro por término de búsqueda
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchesTitle = event.title.toLowerCase().includes(searchLower);
                const matchesContent = (event.content || '').toLowerCase().includes(searchLower);
                
                if (!matchesTitle && !matchesContent) {
                    return false;
                }
            }

            // Filtro por etiquetas seleccionadas
            if (selectedTags.length > 0) {
                let eventTags = [];
                if (event.etiquetas) {
                    if (Array.isArray(event.etiquetas)) {
                        eventTags = event.etiquetas;
                    } else {
                        eventTags = Object.values(event.etiquetas).flat();
                    }
                }
                
                const hasSelectedTag = selectedTags.some(tag => 
                    eventTags.some(eventTag => 
                        eventTag.toLowerCase().includes(tag.toLowerCase())
                    )
                );
                
                if (!hasSelectedTag) {
                    return false;
                }
            }

            return true;
        });
    }, [allEvents, searchTerm, selectedTags, selectedCategory]);

    const handleTagToggle = (tag) => {
        setSelectedTags(prev => 
            prev.includes(tag) 
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    };

    const handleEventClick = (event) => {
        onEventSelect(event);
        onClose();
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedTags([]);
        setSelectedCategory('all');
    };

    const categories = [
        { key: 'all', label: 'Todas las categorías', color: '#666' },
        { key: 'sc', label: 'Star Citizen', color: '#4169E1' },
        { key: 'corvus', label: 'Palar Nest', color: '#FFD700' },
        { key: 'tana', label: 'Tana Novachek', color: '#FF6B6B' },
        { key: 'amber', label: 'Amber Koshobe', color: '#87CEEB' }
    ];

    return (
        <div className="event-search-overlay">
            <div className="event-search-modal">
                <div className="event-search-header">
                    <div className="header-content">
                        <FaSearch className="search-icon" />
                        <h2>Buscador de Eventos</h2>
                    </div>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="event-search-content">
                    {/* Barra de búsqueda mejorada */}
                    <div className="search-input-container">
                        <div className="search-input-wrapper">
                            <FaSearch className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="🔍 Buscar eventos por título o contenido..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button 
                                    className="clear-search"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filtros básicos */}
                    <div className="basic-filters">
                        <div className="filter-section">
                            <h3>
                                <FaFilter className="filter-icon" />
                                Categorías
                            </h3>
                            <div className="category-filters">
                                {categories.map(category => (
                                    <button
                                        key={category.key}
                                        className={`category-filter ${selectedCategory === category.key ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(category.key)}
                                        style={{
                                            borderColor: category.color,
                                            color: selectedCategory === category.key ? '#000000' : category.color,
                                            backgroundColor: selectedCategory === category.key ? category.color : 'transparent'
                                        }}
                                    >
                                        {category.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Botón para filtros avanzados */}
                        <button 
                            className="advanced-filters-toggle"
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        >
                            <FaTags />
                            Filtros Avanzados {showAdvancedFilters ? '(Ocultar)' : '(Mostrar)'}
                        </button>
                    </div>

                    {/* Filtros avanzados */}
                    {showAdvancedFilters && (
                        <div className="advanced-filters">
                            {Object.entries(allTagsByCategory).map(([category, tags]) => (
                                <div key={category} className="filter-section">
                                    <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                                    <div className="tags-container">
                                        {tags.slice(0, 15).map(tag => (
                                            <button
                                                key={tag}
                                                className={`tag-filter ${selectedTags.includes(tag) ? 'active' : ''}`}
                                                onClick={() => handleTagToggle(tag)}
                                            >
                                                {tag}
                                            </button>
                                        ))}
                                    </div>
                                    {tags.length > 15 && (
                                        <p className="tags-note">
                                            Mostrando 15 de {tags.length} {category}
                                        </p>
                                    )}
                                </div>
                            ))}

                            {/* Botón limpiar filtros */}
                            {(searchTerm || selectedTags.length > 0 || selectedCategory !== 'all') && (
                                <button className="clear-filters" onClick={clearFilters}>
                                    <FaTimes />
                                    Limpiar todos los filtros
                                </button>
                            )}
                        </div>
                    )}

                    {/* Resultados */}
                    <div className="search-results">
                        <div className="results-header">
                            <h3>
                                <FaSearch />
                                Resultados ({filteredEvents.length})
                            </h3>
                            {filteredEvents.length > 0 && (
                                <span className="results-info">
                                    Mostrando {filteredEvents.length} de {allEvents.length} eventos
                                </span>
                            )}
                        </div>
                        
                        <div className="events-list">
                            {filteredEvents.length === 0 ? (
                                <div className="no-results">
                                    <FaSearch className="no-results-icon" />
                                    <p>No se encontraron eventos con los filtros seleccionados.</p>
                                    <button className="clear-filters" onClick={clearFilters}>
                                        Limpiar filtros
                                    </button>
                                </div>
                            ) : (
                                filteredEvents.map((event, index) => (
                                    <div 
                                        key={`${event.codigo}-${index}`}
                                        className="event-result"
                                        onClick={() => handleEventClick(event)}
                                    >
                                        <div className="event-result-header">
                                            <h4>{event.title}</h4>
                                            <span className="event-year">{event.year}</span>
                                        </div>
                                        <p className="event-content">{(event.content || '').substring(0, 150)}...</p>
                                        <div className="event-tags">
                                            {(() => {
                                                let tags = [];
                                                if (event.etiquetas) {
                                                    if (Array.isArray(event.etiquetas)) {
                                                        tags = event.etiquetas;
                                                    } else {
                                                        tags = Object.values(event.etiquetas).flat();
                                                    }
                                                }
                                                return (
                                                    <>
                                                        {tags.slice(0, 5).map(tag => (
                                                            <span key={tag} className="event-tag">{tag}</span>
                                                        ))}
                                                        {tags.length > 5 && (
                                                            <span className="more-tags">+{tags.length - 5} más</span>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <div className="event-origin">
                                            <span className="origin-badge">{event.origen}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventSearch; 